import type { Asset } from "building-types/entities/asset";
import { hardCodedAutoPlacedPropLabels } from "building-types/entities/labelTypes";
import type { ExteriorMaterialOverride } from "building-types/materials/materialTypes";
import type { Awning } from "building-types/openings/awning";
import type { Prop, PropAttributeTable } from "building-types/props/propTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { AwningSettings, ShedAwningSettings } from "building-types/settings/awningSettings";
import { AwningType, PropCatalogType } from "building-types/settings/settingsTypesEnum";
import type { AwningViewNoteTable } from "building-types/settings/viewNotesSettings";
import { AwningViewNoteType } from "building-types/settings/viewNotesSettings";
import { ViewEntityType } from "building-types/views/viewEntityType";
import { GLTF_ASSET_SCALE } from "common/constants/assetConstants";
import type { Table } from "common/data/table";
import { assertIsNotNullOrUndefined, assertIsNumber } from "common/utils/assertOfType";
import { radianToDegree } from "common/utils/math";
import uid from "common/utils/uid";
import { getPropSrc } from "doc/elements/propCatalog/propCatalogHelpers";
import { evalFormulas } from "doc/formula/evalFormulas";
import { isFormula } from "doc/formula/formulaParsing";
import { mergedPropLibraryNew, propNew } from "doc/propTools";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { exteriorMaterialOverrideNew } from "doc/utils/materialTools";
import { viewEntityDescriptorNew } from "doc/utils/viewEntityTools";
import { faceNew } from "geom/brep";
import { extrudeFaceAlongNormal } from "geom/extrude";
import type GeomTagGroup from "geom/geomTagGroup";
import {
  planeCopy,
  planeMoveAlongNormal,
  planeEval,
  planeNormal,
  planeByThreePoints,
  planeMove,
  planeEvalDir,
} from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polyLine2Move, polyLine2ExtendEnd, polyLine2ExtendStart, polyLine2Length } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { denormalizeParam } from "geom/utils";
import { vec2AngleBetween } from "geom/vec2";
import { vec3Copy } from "geom/vec3";

export async function addAwning(
  awning: Awning | undefined,
  frontRoom: Room | undefined,
  backRoom: Room | undefined,
  lateralOffset: number,
  wallLength: number,
  awningSettings: AwningSettings | undefined,
  propCatalog: PropAttributeTable,
  assets: Table<Asset>,
  openingPlane: Plane,
  awningBottomLine: PolyLine2,
  extrusions: GeomTagGroup<ExteriorMaterialOverride>,
  awningRoof: GeomTagGroup<ExteriorMaterialOverride>,
  props: Prop[],
  viewNotes: AwningViewNoteTable,
  mirror: boolean,
): Promise<void> {
  const isExterior = !backRoom || !isInteriorRoomEnclosure[backRoom.attributes.roomEnclosure];
  if (isExterior) {
    assertIsNotNullOrUndefined(awningSettings);
    switch (awningSettings.awningType) {
      case AwningType.SHED:
        await shedAwning(
          awning,
          frontRoom,
          lateralOffset,
          wallLength,
          awningSettings,
          propCatalog,
          assets,
          openingPlane,
          awningBottomLine,
          extrusions,
          awningRoof,
          props,
          viewNotes,
          mirror,
        );
        break;
      case AwningType.DISABLED:
        break;
    }
  }
}

const AWNING_ROOF_THICKNESS = 0.05;

async function shedAwning(
  awning: Awning | undefined,
  room: Room | undefined,
  lateralOffset: number,
  wallLength: number,
  shedAwningSettings: ShedAwningSettings,
  propCatalog: PropAttributeTable,
  assets: Table<Asset>,
  openingPlane: Plane,
  awningBottomLine: PolyLine2,
  extrusions: GeomTagGroup<ExteriorMaterialOverride>,
  awningRoof: GeomTagGroup<ExteriorMaterialOverride>,
  props: Prop[],
  viewNotes: AwningViewNoteTable,
  mirror: boolean,
): Promise<void> {
  const slopeOrig = shedAwningSettings.slope;
  // The slope of an awning can potentially be a formula (eg "2/3"). Evaluate it here.
  const slope = !isFormula(slopeOrig) ? slopeOrig : evalFormulas(slopeOrig);

  const depth = shedAwningSettings.depth;
  const fasciaHeight = shedAwningSettings.fasciaHeight;
  const trimHeight = shedAwningSettings.trimHeight;
  const trimWidth = shedAwningSettings.trimWidth;
  const bracketOffset = shedAwningSettings.bracketOffset;
  const awningWidthOffset = shedAwningSettings.awningWidthOffset ?? 0;
  const awningHeightOffset = shedAwningSettings.awningHeightOffset ?? 0;
  const bracketTrimSpacing = shedAwningSettings.bracketTrimSpacing;
  const hasSoffit = shedAwningSettings.soffit;

  const soffitHeight = hasSoffit ? shedAwningSettings.soffitHeight ?? 6 : 0;
  const soffitDepthOffset = hasSoffit ? shedAwningSettings.soffitDepthOffset ?? 2 : 0;
  const soffitWidthOffset = hasSoffit ? shedAwningSettings.soffitWidthOffset ?? 2 : 0;

  polyLine2Move(awningBottomLine, 0, awningHeightOffset + soffitHeight);

  const roofMat = shedAwningSettings.roofMaterial;

  const propId = shedAwningSettings.awningBracketPropId;
  const mergedPropLibrary = mergedPropLibraryNew(assets);
  const propSrc = await getPropSrc(propCatalog, propId, mergedPropLibrary);

  const propWidth = propSrc ? (propSrc.boundingBox.maxX - propSrc.boundingBox.minX) * GLTF_ASSET_SCALE : 0;

  assertIsNumber(bracketTrimSpacing);

  const awningExtend = awningWidthOffset + bracketTrimSpacing + propWidth;
  const startExtend =
    awning && awning.startOffset !== undefined
      ? denormalizeParam(wallLength, awning.startOffset, !!awning.offsetsNormalized)
      : 0;
  const endExtend =
    awning && awning.endOffset !== undefined
      ? denormalizeParam(wallLength, awning.endOffset, !!awning.offsetsNormalized)
      : 0;
  polyLine2ExtendEnd(awningBottomLine, awningExtend + (mirror ? startExtend : endExtend));
  polyLine2ExtendStart(awningBottomLine, awningExtend + (mirror ? endExtend : startExtend));

  let awningPlane = planeCopy(openingPlane);
  if (lateralOffset !== 0) {
    awningPlane = planeMoveAlongNormal(awningPlane, lateralOffset);
  }

  const offset = planeEval(awningPlane, awningBottomLine[0], awningBottomLine[1]);
  awningPlane.originX = offset[0];
  awningPlane.originY = offset[1];
  // awningPlane.originZ = offset[2];
  const awningNormal = vec3Copy(planeNormal(awningPlane));

  assertIsNumber(depth);
  const height = depth * slope;
  const awningLength = polyLine2Length(awningBottomLine);
  const extrusionAmt = 1;

  // Sides
  const oz = awningPlane.originZ + awningBottomLine[1];
  const sidePlane = planeByThreePoints(
    awningPlane.originX,
    awningPlane.originY,
    oz,
    awningPlane.originX + awningNormal[0],
    awningPlane.originY + awningNormal[1],
    oz + awningNormal[2],
    awningPlane.originX,
    awningPlane.originY,
    oz + 1,
  );

  if (!sidePlane) {
    return;
  }

  assertIsNumber(fasciaHeight);
  assertIsNumber(trimWidth);
  assertIsNumber(trimHeight);

  const sideLine = polyLine2New([
    depth,
    0,
    0,
    0,
    0,
    height + fasciaHeight,
    depth + trimWidth,
    fasciaHeight, // out
    depth + trimWidth,
    fasciaHeight - trimHeight, // down
    depth,
    fasciaHeight - trimHeight, // in
    depth,
    0,
  ]);

  const sideFace = faceNew([sideLine], sidePlane);

  const awningDescriptor = viewEntityDescriptorNew(uid(), ViewEntityType.AWNING, viewNotes[AwningViewNoteType.AWNING]);
  const bracketDescriptor = viewEntityDescriptorNew(
    uid(),
    ViewEntityType.AWNING_BRACKET,
    viewNotes[AwningViewNoteType.BRACKET],
  );

  extrusions.add(
    extrudeFaceAlongNormal(sideFace, -awningLength),
    exteriorMaterialOverrideNew("trimExterior0", undefined, awningDescriptor),
  );

  if (hasSoffit) {
    const soffitOutline = polyLine2New([
      soffitWidthOffset,
      0,
      soffitWidthOffset,
      soffitHeight,
      awningLength - soffitWidthOffset,
      soffitHeight,
      awningLength - soffitWidthOffset,
      0,
      soffitWidthOffset,
      0,
    ]);

    const soffitPlane = planeCopy(awningPlane);

    // oz already includes awningPlane.originZ so we need to subtract it out
    planeMove(soffitPlane, 0, 0, oz - awningPlane.originZ - soffitHeight);
    const soffitFace = faceNew([soffitOutline], soffitPlane);
    extrusions.add(
      extrudeFaceAlongNormal(soffitFace, depth - soffitDepthOffset),
      exteriorMaterialOverrideNew("trimExterior0", undefined, awningDescriptor),
    );
  }

  const uVec = vec3Copy(planeEvalDir(awningPlane, 1.0, 0.0));

  // Top
  const topPlane = planeByThreePoints(
    // origin
    awningPlane.originX,
    awningPlane.originY,
    oz + height + fasciaHeight,
    // u
    awningPlane.originX - uVec[0],
    awningPlane.originY - uVec[1],
    oz + height + fasciaHeight,
    // v
    awningPlane.originX + (depth + trimWidth) * awningNormal[0],
    awningPlane.originY + (depth + trimWidth) * awningNormal[1],
    oz + fasciaHeight,
  );

  // It's possible topPlane will be an invalid plane so we need to check for its existence
  // before operating on it
  if (topPlane) {
    const topDepth = Math.sqrt(height * height + depth * depth) + extrusionAmt;
    const topPolyLine = polyLine2New([0, 0, -awningLength, 0, -awningLength, topDepth, 0, topDepth, 0, 0]);
    const topFace = faceNew([topPolyLine], topPlane);

    awningRoof.add(topFace, exteriorMaterialOverrideNew(roofMat, undefined, awningDescriptor));

    extrusions.add(
      extrudeFaceAlongNormal(topFace, AWNING_ROOF_THICKNESS),
      exteriorMaterialOverrideNew(roofMat, undefined, awningDescriptor),
    );
  }

  if (shedAwningSettings.awningBracketPropId && propSrc) {
    const propZ = propSrc.boundingBox.maxZ * GLTF_ASSET_SCALE;
    const rotationDegrees = radianToDegree(vec2AngleBetween(0, -1, awningNormal[0], awningNormal[1]));
    const x = awningPlane.originX;
    const y = awningPlane.originY;
    const z = oz - propZ;

    assertIsNumber(bracketOffset);

    if (propId) {
      const startBracket = propNew({
        room: room ?? null,
        src: propId,
        x: x + bracketOffset * uVec[0],
        y: y + bracketOffset * uVec[1],
        z: z - soffitHeight,
        rotationDegrees,
        descriptor: bracketDescriptor,
        catalogType: PropCatalogType.PROP_CATALOG,
        isExterior: true,
        labels: hardCodedAutoPlacedPropLabels, // We could consider making this configurable.
      });
      props.push(startBracket);

      const bracketWidth = (propSrc.boundingBox.maxX - propSrc.boundingBox.minX) * GLTF_ASSET_SCALE;
      const endBrackOffset = awningLength - bracketWidth - bracketOffset;
      const endBracket = propNew({
        room: room ?? null,
        src: propId,
        x: x + endBrackOffset * uVec[0],
        y: y + endBrackOffset * uVec[1],
        z: z - soffitHeight,
        rotationDegrees,
        descriptor: bracketDescriptor,
        catalogType: PropCatalogType.PROP_CATALOG,
        isExterior: true,
        labels: hardCodedAutoPlacedPropLabels, // We could consider making this configurable.
      });
      props.push(endBracket);
    }
  }
}
